import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { EASE } from '../../../lib/motion';
import styles from '../Home.module.css';

/**
 * Film-title entrance — the homepage opens the way a Muse client
 * gallery does: a credit eyebrow between hairlines that draw outward,
 * a serif title rising off the press, a folio line, then the black
 * frame parts like a screening-room gate with a blade of light at the
 * seam. Gated once per full page load by the caller (see
 * introPlayedThisLoad); click or Esc skips; never mounted under
 * prefers-reduced-motion. aria-hidden, zero focusables, transform and
 * opacity only.
 */
const FilmTitleIntro: React.FC<{
  onReveal: () => void;
  onExit: () => void;
}> = ({ onReveal, onExit }) => {
  const [gateOpen, setGateOpen] = useState(false);

  const skip = useCallback(() => {
    setGateOpen((already) => {
      if (!already) onReveal();
      return true;
    });
  }, [onReveal]);

  useEffect(() => {
    const timer = window.setTimeout(skip, 2300);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skip();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [skip]);

  return (
    <div
      className={`${styles.intro} ${gateOpen ? styles.introOpen : ''}`}
      aria-hidden="true"
      onClick={skip}
    >
      {/* The gate: two black panels that part to reveal the hero */}
      <motion.div
        className={`${styles.introPanel} ${styles.introPanelTop}`}
        initial={false}
        animate={gateOpen ? { y: '-100%' } : { y: '0%' }}
        transition={{ duration: 0.95, ease: EASE, delay: gateOpen ? 0.2 : 0 }}
        onAnimationComplete={() => {
          if (gateOpen) onExit();
        }}
      />
      <motion.div
        className={`${styles.introPanel} ${styles.introPanelBottom}`}
        initial={false}
        animate={gateOpen ? { y: '100%' } : { y: '0%' }}
        transition={{ duration: 0.95, ease: EASE, delay: gateOpen ? 0.2 : 0 }}
      />

      {/* A blade of light at the seam as the gate cracks open */}
      {gateOpen && (
        <motion.div
          className={styles.introSeam}
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: [0, 1, 0] }}
          transition={{ duration: 1.05, ease: EASE }}
        />
      )}

      <motion.div
        className={styles.introTitles}
        animate={gateOpen ? { opacity: 0, y: -16 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.32, ease: EASE }}
      >
        <div className={styles.introEyebrowRow}>
          <motion.span
            className={`${styles.introRule} ${styles.introRuleLeft}`}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          />
          <motion.span
            className={styles.introEyebrow}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35, ease: EASE }}
          >
            Muse presents
          </motion.span>
          <motion.span
            className={`${styles.introRule} ${styles.introRuleRight}`}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          />
        </div>

        <span className={styles.introTitleMask}>
          <motion.span
            className={styles.introTitle}
            initial={{ y: '112%' }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay: 0.5, ease: EASE }}
          >
            Your photography companion.
          </motion.span>
        </span>

        <motion.p
          className={styles.introFolio}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.2, ease: EASE }}
        >
          On a set near you
        </motion.p>
      </motion.div>

      <motion.div
        className={styles.introGrain}
        animate={gateOpen ? { opacity: 0 } : { opacity: 1 }}
        transition={{ duration: 0.4 }}
      />
    </div>
  );
};

export default FilmTitleIntro;
